import { LoginTypes, loginUser } from '@api/login';
import Card from '@components/Atoms/Card';
import DefaultButton from '@components/Atoms/DefaultButton';
import Img from '@components/Atoms/Img';
import LinkText from '@components/Atoms/Link';
import LoginForm from '@components/Organisms/LoginForm';
import { LoginMethods } from '@enums/LoginMethods';
import { buildCheckYourInboxPath } from '@helpers/auth.helper';
import useIsMobileApp from '@hooks/useIsMobileApp';
import useWindowDimensions from '@hooks/useWindowDimensions';
import { CompanyLoginMethodTypesInterface } from '@interfaces/index';
import AuthenticationLayout from '@layouts/AuthenticationLayout';
import { showToast } from '@shared/atoms';
import { LoadingBackdrop } from '@shared/molecules';
import { ToastMessages } from 'constants/toastMessages';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import styles from './index.module.scss';

const Login: React.FunctionComponent<LoginProps> = (props) => {
  const {
    content,
    organizationLogo,
    organizationAltText,
    organizationId,
    companyURL,
    hideHapstarLogo,
    hideSignUp,
    platformName,
  } = props;
  const { isMobile } = useWindowDimensions();
  const isMobileApp = useIsMobileApp();
  const hideHapstarLogoEnabled = hideHapstarLogo;
  const [loginMethodsToDisplay, setLoginMethodsToDisplay] = useState<
    CompanyLoginMethodTypesInterface[] | []
  >([]);
  const [userEmail, setUserEmail] = useState<string>('');
  const history = useHistory();
  const [loadingLoginRequest, setLoadingLoginRequest] = useState(false);
  const [rememberMe, setRememberMe] = useState<boolean>(true);

  useEffect(() => {
    localStorage.setItem('rememberMe', rememberMe.toString());
  }, [rememberMe]);

  const handleOnBack = async () => {
    setLoginMethodsToDisplay([]);
  };

  const sendLoginMagicRequest = async (email: string) => {
    const type = isMobileApp ? LoginTypes.TOKEN : LoginTypes.LINK;
    setLoadingLoginRequest(true);

    try {
      const response = await loginUser({
        email: email.toLowerCase(),
        type,
        organizationId,
        remember: rememberMe,
      });

      if (response.ok) {
        showToast(ToastMessages.auth.info.EMAIL_SENT_TO_YOUR_INBOX, undefined, 'info');
        navigateToCheckInbox(email);
      } else if (response.status === 400 || response.status === 403) {
        showToast(ToastMessages.auth.info.WE_COULD_NOT_RECOGNISE_THIS_EMAIL, undefined, 'warning');
      } else {
        showToast(ToastMessages.auth.error.LOGIN_UNSUCCESSFUL_CONTACT_ADMIN, undefined, 'error');
      }
    } catch (error) {
      showToast(ToastMessages.common.UNEXPECTED_ERROR_TRY_AGAIN, undefined, 'error');
      setLoadingLoginRequest(false);
    } finally {
      setLoadingLoginRequest(false);
    }
  };

  const navigateToCheckInbox = (email: string) => {
    history.push(
      buildCheckYourInboxPath(email, {
        companyURL,
        isMobileApp,
      }),
    );
  };

  const handleLoginMethodClick = async (loginMethod: CompanyLoginMethodTypesInterface) => {
    const isSSO = loginMethod?.loginMethod === LoginMethods.SSO;
    const isLink = loginMethod?.loginMethod === LoginMethods.LINK;

    if (isMobileApp) {
      await sendLoginMagicRequest(userEmail);
    } else if (!isMobileApp) {
      if (isSSO) {
        showToast(ToastMessages.auth.info.REDIRECTING_TO_SINGLE_SIGN_ON, undefined, 'info');
        window.location.replace(loginMethod?.loginUrl);
      } else if (isLink) {
        // Call sendLoginMagicRequest for LINK login method
        await sendLoginMagicRequest(userEmail);
      } else {
        await sendLoginMagicRequest(userEmail);
      }
    }
  };

  const handleLoginMethodsToDisplay = (
    methods: CompanyLoginMethodTypesInterface[],
    email: string,
  ) => {
    setLoginMethodsToDisplay(methods);
    if (email) {
      setUserEmail(email);
    } else {
      showToast(ToastMessages.auth.info.PLEASE_ENTER_A_VALID_EMAIL_ADDRESS, undefined, 'warning');
    }
  };

  return (
    <AuthenticationLayout
      title="Login"
      organizationLogo={organizationLogo}
      organizationAltText={organizationAltText}
      hideHapstarLogoEnabled={hideHapstarLogoEnabled}
    >
      {loadingLoginRequest && <LoadingBackdrop text="Preparing your magic link..." />}
      <div className={styles.cardContainer}>
        {isMobileApp && (
          <div className={styles.subHeading}>
            <h1>&quot;Unlocking the power of happiness&quot;</h1>
          </div>
        )}
        <Card>
          <div className={styles.loginContentContainer}>
            <div className={styles.loginContent}>
              {loginMethodsToDisplay && loginMethodsToDisplay?.length > 1 ? (
                <div className={styles.content}>
                  <h1>Select Login option</h1>
                  {loginMethodsToDisplay.map((login, index) => (
                    <div className={styles.buttonHolder} key={index}>
                      <DefaultButton
                        variant="primary"
                        type="button"
                        onClick={() => handleLoginMethodClick(login)}
                        disabled={loadingLoginRequest}
                      >
                        {login.loginMethodText}
                      </DefaultButton>
                    </div>
                  ))}

                  <div className={styles.goBack}>
                    <LinkText onClick={handleOnBack}>Back</LinkText>
                  </div>
                </div>
              ) : (
                <>
                  {organizationLogo && isMobile && !hideHapstarLogo && (
                    <div className={styles.organizationLogoHolder}>
                      <Img
                        alt={organizationAltText || 'Organization logo'}
                        src={organizationLogo}
                        className={styles.img}
                      />
                    </div>
                  )}
                  <h1>{platformName ? `Login to ${platformName}` : 'Login to Hapstar'}</h1>
                  <div className={styles.loginDetails}>
                    {content && content}
                    <LoginForm
                      organizationId={organizationId}
                      companyURL={companyURL}
                      handleLoginMethodsToDisplay={handleLoginMethodsToDisplay}
                      rememberMe={rememberMe}
                      setRememberMe={setRememberMe}
                    />
                    {!hideSignUp && (
                      <div className={styles.gotAccountHolder}>
                        <p>
                          Don&apos;t have an account? <br />
                          <LinkText
                            path={`/signup${companyURL ? `/${companyURL}` : ''}${
                              isMobileApp ? '?isMobileApp=true' : ''
                            }`}
                          >
                            Sign up
                          </LinkText>
                        </p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </Card>
      </div>
    </AuthenticationLayout>
  );
};

Login.displayName = 'Login';

Login.defaultProps = {
  content: undefined,
  organizationLogo: undefined,
  organizationAltText: undefined,
  organizationId: 0,
  companyURL: undefined,
  hideHapstarLogo: false,
  hideSignUp: false,
  platformName: null,
};

interface LoginProps
  extends React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> {
  content?: JSX.Element | string | undefined;
  organizationLogo?: string | undefined;
  organizationAltText?: string | undefined;
  organizationId?: number;
  companyURL?: string;
  hideHapstarLogo?: boolean;
  hideSignUp?: boolean;
  platformName?: string | null;
}

export default Login;
